import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
const HOST_CLASS = 'textarea-mic-host';
const QUILL_HOST_CLASS = 'is-quill-field';
const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;
/** SurveyJS question property that opts a comment field into the rich-text editor. */
const QUILL_RENDER_AS = 'quill';
const MIN_EDITOR_HEIGHT = 64;
const QUILL_TOOLBAR = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [
        { color: ['#000000', '#FF00FF', '#FF0000', '#00FF00', '#0000FF'] },
        { background: ['#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF'] },
    ],
    [{ script: 'sub' }, { script: 'super' }],
    ['link']
];
function MicrophoneIcon() {
    return (_jsxs("svg", { className: "microphone-icon", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: [_jsx("path", { d: "M12 15.25a3.75 3.75 0 0 0 3.75-3.75v-5a3.75 3.75 0 0 0-7.5 0v5A3.75 3.75 0 0 0 12 15.25Z", stroke: "currentColor", strokeWidth: "1.8" }), _jsx("path", { d: "M5.75 10.75v.75a6.25 6.25 0 0 0 12.5 0v-.75M12 17.75v3M8.75 20.75h6.5", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }));
}
function findQuestionTarget(textarea, survey) {
    if (!survey)
        return null;
    try {
        const questions = survey.getAllQuestions(false, false, true);
        const id = textarea.id;
        if (id) {
            const byInput = questions.find((question) => question.inputId === id);
            if (byInput)
                return { question: byInput, field: 'value' };
            const byComment = questions.find((question) => question.commentId === id);
            if (byComment)
                return { question: byComment, field: 'comment' };
        }
        const name = textarea.getAttribute('data-name') ||
            textarea.closest('[data-name]')?.getAttribute('data-name') ||
            textarea.getAttribute('name') ||
            '';
        if (!name)
            return null;
        const question = questions.find((entry) => entry.name === name) ??
            survey.getQuestionByValueName(name) ??
            survey.getQuestionByName(name) ??
            null;
        return question ? { question, field: 'value' } : null;
    }
    catch {
        return null;
    }
}
/** Returns the question when its JSON asks for the rich-text editor. */
function findQuillQuestion(textarea, survey) {
    const target = findQuestionTarget(textarea, survey);
    if (!target || target.field !== 'value')
        return null;
    const renderAs = target.question.renderAs;
    return String(renderAs ?? '').toLowerCase() === QUILL_RENDER_AS ? target.question : null;
}
/**
 * Write text/HTML back to the form. SurveyJS renders controlled inputs, so
 * the model is the source of truth; the DOM write is only a fallback.
 */
function writeValue(textarea, value, survey) {
    const target = findQuestionTarget(textarea, survey);
    if (target && target.question.isReadOnly !== true) {
        if (target.field === 'comment') {
            target.question.comment = value;
        }
        else {
            target.question.value = value;
        }
        return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value');
    descriptor?.set?.call(textarea, value);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    textarea.dispatchEvent(new Event('change', { bubbles: true }));
}
function looksLikeHtml(value) {
    return HTML_TAG_RE.test(value);
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function toEditorHtml(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return '';
    if (looksLikeHtml(trimmed))
        return trimmed;
    return trimmed
        .split(/\n{2,}/)
        .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
        .join('');
}
function normalizeEditorHtml(html) {
    const trimmed = html.replace(/<p><br><\/p>/gi, '').trim();
    if (!trimmed || trimmed === '<p></p>')
        return '';
    return trimmed;
}
function ensureHost(textarea) {
    const parent = textarea.parentElement;
    if (!parent)
        return null;
    parent.classList.add(HOST_CLASS);
    return parent;
}
function shouldSkipTextarea(textarea) {
    if (textarea.readOnly || textarea.disabled)
        return true;
    if (textarea.dataset.noMic === 'true')
        return true;
    if (textarea.closest('.chat-input-area'))
        return true;
    if (textarea.closest('.sd-element--readonly, .sv_q_readonly'))
        return true;
    return false;
}
function collectTextareas(root) {
    return Array.from(root.querySelectorAll('textarea')).filter((textarea) => !shouldSkipTextarea(textarea));
}
/**
 * Quill's editor layer covers the container corner, so the browser's own resize
 * grip is unreachable. This strip drags the container height instead.
 */
function startResize(container, handle, event) {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = container.getBoundingClientRect().height;
    const onMove = (moveEvent) => {
        const height = Math.max(MIN_EDITOR_HEIGHT, startHeight + (moveEvent.clientY - startY));
        container.style.height = `${height}px`;
    };
    const onEnd = () => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onEnd);
        handle.removeEventListener('pointercancel', onEnd);
        handle.classList.remove('is-dragging');
        if (handle.hasPointerCapture(event.pointerId)) {
            handle.releasePointerCapture(event.pointerId);
        }
    };
    handle.setPointerCapture(event.pointerId);
    handle.classList.add('is-dragging');
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
}
function createResizeHandle(container) {
    const handle = document.createElement('div');
    handle.className = 'textarea-quill-resize';
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-label', 'Drag to resize the editor');
    handle.title = 'Drag to resize';
    handle.addEventListener('pointerdown', (event) => startResize(container, handle, event));
    return handle;
}
/** Replace the dictated tail so interim speech results do not duplicate. */
function writeDictationToQuill(session, text) {
    const { quill, dictation } = session;
    if (!dictation)
        return;
    const insert = text ? `${dictation.prefix}${text}` : '';
    const end = Math.max(quill.getLength() - 1, dictation.start);
    if (end > dictation.start) {
        quill.deleteText(dictation.start, end - dictation.start, 'user');
    }
    if (insert) {
        quill.insertText(dictation.start, insert, 'user');
    }
    quill.setSelection(dictation.start + insert.length, 0, 'silent');
}
export function TextareaMicOverlay({ containerRef, survey }) {
    const [hosts, setHosts] = useState([]);
    const [revision, setRevision] = useState(0);
    const [activeTextarea, setActiveTextarea] = useState(null);
    const [draft, setDraft] = useState('');
    const activeRef = useRef(null);
    const surveyRef = useRef(survey);
    const pendingStartRef = useRef(null);
    const idMapRef = useRef(new WeakMap());
    const nextIdRef = useRef(0);
    const refreshTimerRef = useRef(null);
    const quillSessionsRef = useRef(new Map());
    activeRef.current = activeTextarea;
    surveyRef.current = survey;
    const applyDraft = useCallback((next) => {
        setDraft(next);
        const target = activeRef.current;
        if (!target)
            return;
        const session = quillSessionsRef.current.get(target);
        if (session?.dictation) {
            writeDictationToQuill(session, next);
            return;
        }
        writeValue(target, next, surveyRef.current ?? null);
    }, []);
    const { isListening, supported, startListening, stopListening } = useSpeechRecognition(draft, applyDraft);
    const textareaId = useCallback((textarea) => {
        const existing = idMapRef.current.get(textarea);
        if (existing)
            return existing;
        const id = `mic-ta-${nextIdRef.current++}`;
        idMapRef.current.set(textarea, id);
        return id;
    }, []);
    const refreshHosts = useCallback(() => {
        const root = containerRef.current;
        if (!root) {
            setHosts([]);
            return;
        }
        const next = [];
        for (const textarea of collectTextareas(root)) {
            const host = ensureHost(textarea);
            if (!host)
                continue;
            next.push({ id: textareaId(textarea), textarea, host });
        }
        setHosts((prev) => {
            const same = prev.length === next.length &&
                prev.every((entry, index) => entry.textarea === next[index].textarea && entry.host === next[index].host);
            return same ? prev : next;
        });
        setRevision((tick) => tick + 1);
    }, [containerRef, textareaId]);
    const scheduleRefresh = useCallback(() => {
        if (refreshTimerRef.current)
            clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            refreshHosts();
        }, 50);
    }, [refreshHosts]);
    const unmountQuill = useCallback((textarea) => {
        const session = quillSessionsRef.current.get(textarea);
        if (!session)
            return;
        session.quill.off('text-change');
        session.wrap.remove();
        quillSessionsRef.current.delete(textarea);
        textarea.parentElement?.classList.remove(QUILL_HOST_CLASS);
    }, []);
    const mountQuill = useCallback((textarea) => {
        const host = textarea.parentElement;
        if (!host)
            return;
        const wrap = document.createElement('div');
        wrap.className = 'textarea-quill-wrap';
        textarea.insertAdjacentElement('afterend', wrap);
        const quill = new Quill(wrap, {
            theme: 'snow',
            modules: { toolbar: QUILL_TOOLBAR },
        });
        const session = { quill, wrap, syncing: false, dictation: null };
        const html = toEditorHtml(textarea.value ?? '');
        if (html) {
            quill.clipboard.dangerouslyPasteHTML(html);
        }
        quill.on('text-change', () => {
            if (session.syncing)
                return;
            writeValue(textarea, normalizeEditorHtml(quill.root.innerHTML), surveyRef.current ?? null);
        });
        wrap.appendChild(createResizeHandle(wrap));
        host.classList.add(QUILL_HOST_CLASS);
        quillSessionsRef.current.set(textarea, session);
    }, []);
    useEffect(() => {
        const root = containerRef.current;
        if (!root)
            return;
        refreshHosts();
        const observer = new MutationObserver((mutations) => {
            const ignore = mutations.every((mutation) => {
                const target = mutation.target;
                if (!(target instanceof Element))
                    return false;
                return Boolean(target.closest('.textarea-quill-wrap, .ql-toolbar, .ql-container'));
            });
            if (!ignore)
                scheduleRefresh();
        });
        observer.observe(root, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            if (refreshTimerRef.current)
                clearTimeout(refreshTimerRef.current);
        };
    }, [containerRef, refreshHosts, scheduleRefresh, survey]);
    /** Keep one Quill editor mounted per renderAs: "quill" textarea that is on screen. */
    useEffect(() => {
        const sessions = quillSessionsRef.current;
        for (const [textarea, session] of [...sessions]) {
            const stale = !textarea.isConnected ||
                !session.wrap.isConnected ||
                !findQuillQuestion(textarea, survey ?? null);
            if (stale)
                unmountQuill(textarea);
        }
        for (const { textarea } of hosts) {
            if (sessions.has(textarea))
                continue;
            if (!findQuillQuestion(textarea, survey ?? null))
                continue;
            mountQuill(textarea);
        }
    }, [hosts, revision, survey, mountQuill, unmountQuill]);
    /** Reflect model-side changes (smart fill, resets) into the open editors. */
    useEffect(() => {
        if (!survey)
            return;
        const sync = () => {
            for (const [textarea, session] of quillSessionsRef.current) {
                if (session.dictation)
                    continue;
                const question = findQuillQuestion(textarea, survey);
                if (!question)
                    continue;
                const incoming = question.value == null ? '' : String(question.value);
                if (normalizeEditorHtml(incoming) === normalizeEditorHtml(session.quill.root.innerHTML)) {
                    continue;
                }
                session.syncing = true;
                const html = toEditorHtml(incoming);
                if (html) {
                    session.quill.clipboard.dangerouslyPasteHTML(html, 'silent');
                }
                else {
                    session.quill.setText('', 'silent');
                }
                session.syncing = false;
            }
        };
        survey.onValueChanged.add(sync);
        return () => survey.onValueChanged.remove(sync);
    }, [survey]);
    const beginListening = useCallback((textarea) => {
        const session = quillSessionsRef.current.get(textarea);
        activeRef.current = textarea;
        setActiveTextarea(textarea);
        if (session) {
            const start = Math.max(session.quill.getLength() - 1, 0);
            const existing = session.quill.getText(0, start);
            session.dictation = { start, prefix: existing.trim() ? ' ' : '' };
            setDraft('');
            startListening('');
            return;
        }
        const base = textarea.value ?? '';
        setDraft(base);
        startListening(base);
    }, [startListening]);
    useEffect(() => {
        if (isListening)
            return;
        for (const session of quillSessionsRef.current.values()) {
            session.dictation = null;
        }
        const queued = pendingStartRef.current;
        if (!queued)
            return;
        pendingStartRef.current = null;
        if (queued.isConnected)
            beginListening(queued);
    }, [isListening, beginListening]);
    useEffect(() => {
        return () => {
            stopListening();
            for (const textarea of [...quillSessionsRef.current.keys()]) {
                unmountQuill(textarea);
            }
        };
    }, [stopListening, unmountQuill]);
    const handleMicToggle = (textarea) => {
        if (!supported)
            return;
        if (isListening) {
            const sameField = activeRef.current === textarea;
            pendingStartRef.current = sameField ? null : textarea;
            stopListening();
            return;
        }
        beginListening(textarea);
    };
    if (!supported || hosts.length === 0)
        return null;
    return (_jsx(_Fragment, { children: hosts.map(({ id, textarea, host }) => {
            const listening = isListening && activeTextarea === textarea;
            return createPortal(_jsx("div", { className: "textarea-field-tools", children: _jsx("div", { className: "textarea-field-actions", children: _jsx("button", { type: "button", className: `btn btn-secondary microphone-button textarea-tool-button textarea-mic-button${listening ? ' listening' : ''}`, "aria-label": listening ? 'Stop dictation' : 'Dictate into this field', title: listening ? 'Stop dictation' : 'Dictate into this field', onMouseDown: (event) => event.preventDefault(), onClick: () => handleMicToggle(textarea), children: _jsx(MicrophoneIcon, {}) }) }) }, id), host);
        }) }));
}
